import { z } from 'zod';
import { RECIPE_FORMAT_VERSION } from './versions';

export const approvalModeSchema = z.enum(['none', 'plan', 'before_step', 'after_step']);

export const recipeReviewerSchema = z.object({
  /** slug of a RoleTemplate (reviewer role) or an explicit AgentInstance id */
  role: z.string().min(1),
  /** checklist skill slugs the reviewer must run */
  checklists: z.array(z.string().min(1)).min(1),
});

export const recipeStepSchema = z.object({
  id: z.string().regex(/^[a-z0-9_]+$/),
  name: z.string().min(1),
  /** RoleTemplate slug that performs the step */
  role: z.string().min(1),
  skills: z.array(z.string().min(1)).default([]),
  reviewers: z.array(recipeReviewerSchema).default([]),
  /** artifact types expected as inputs */
  inputs: z.array(z.string().min(1)).default([]),
  /** artifact types produced */
  outputs: z.array(z.string().min(1)).default([]),
  dependsOn: z.array(z.string().min(1)).default([]),
  approval: approvalModeSchema.default('none'),
  humanStep: z.boolean().default(false),
  brief: z.string().optional(),
});

export const recipeDefinitionSchema = z
  .object({
    formatVersion: z.literal(RECIPE_FORMAT_VERSION),
    steps: z.array(recipeStepSchema).min(1),
  })
  .superRefine((def, ctx) => {
    const ids = new Set(def.steps.map((s) => s.id));
    if (ids.size !== def.steps.length) {
      ctx.addIssue({ code: 'custom', message: 'step ids must be unique' });
    }
    for (const step of def.steps) {
      for (const dep of step.dependsOn) {
        if (!ids.has(dep)) {
          ctx.addIssue({ code: 'custom', message: `step "${step.id}" depends on unknown step "${dep}"` });
        }
        if (dep === step.id) {
          ctx.addIssue({ code: 'custom', message: `step "${step.id}" depends on itself` });
        }
      }
    }
    // cycle detection (DFS)
    const state = new Map<string, 0 | 1 | 2>();
    const byId = new Map(def.steps.map((s) => [s.id, s]));
    const visit = (id: string): boolean => {
      const st = state.get(id) ?? 0;
      if (st === 1) return true;
      if (st === 2) return false;
      state.set(id, 1);
      for (const dep of byId.get(id)?.dependsOn ?? []) {
        if (byId.has(dep) && visit(dep)) return true;
      }
      state.set(id, 2);
      return false;
    };
    for (const step of def.steps) {
      if (visit(step.id)) {
        ctx.addIssue({ code: 'custom', message: `dependency cycle through step "${step.id}"` });
        break;
      }
    }
  });

export type RecipeDefinition = z.infer<typeof recipeDefinitionSchema>;
export type RecipeStep = z.infer<typeof recipeStepSchema>;
